/**
 * MAITAI 共通暗号ユーティリティ
 *
 * Web Crypto API (crypto.subtle) のみを使う。
 * ブラウザ / Node.js 20+ / Cloudflare Workers のいずれでも同一コードで動作する
 * ことを前提としているため、node:crypto 等の環境依存 API は使用しないこと。
 *
 * 脅威モデル(CLAUDE.md §9-6):
 *   secret_cp は PWA バンドルに埋め込まれるため、技術力のある攻撃者には
 *   抽出され得る。ここでの署名は「URL の推測・自作を防ぐ」ことが目的であり、
 *   完全な偽造耐性は GPS 照合とサーバー側検証で補う。
 */

const enc = new TextEncoder();

/** base64url エンコード(パディングなし) */
export function base64url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** base64url デコード */
export function base64urlDecode(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  const bin = atob(b64 + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** HMAC-SHA256。secret は base64url 文字列でも生の文字列でも可(UTF-8 として扱う)。 */
export async function hmacSha256(secret: string, message: string): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(message));
  return new Uint8Array(sig);
}

/** HMAC-SHA256 を base64url 文字列で返す(先頭 length 文字に切り詰め) */
export async function hmacB64u(secret: string, message: string, length?: number): Promise<string> {
  const s = base64url(await hmacSha256(secret, message));
  return length === undefined ? s : s.slice(0, length);
}

/**
 * 文字列の定数時間比較。
 * 長さの違いは漏れるが、トークン長は固定なので実用上問題ない。
 */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** 暗号論的乱数から base64url 文字列を生成(秘密鍵生成用) */
export function randomSecret(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64url(buf);
}

/** 0 以上 max 未満の一様乱数(モジュロバイアスを除去) */
export function randomInt(max: number): number {
  if (max <= 0) throw new Error("max must be > 0");
  const limit = Math.floor(0xffffffff / max) * max;
  const buf = new Uint32Array(1);
  let v: number;
  do {
    crypto.getRandomValues(buf);
    v = buf[0]!;
  } while (v >= limit);
  return v % max;
}

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Crockford Base32 で符号化する。
 *
 * 人が読み上げ、人が打ち込む文字列に使う。この符号化は I・L・O・U を含まず、
 * 大小を区別しない。受付で「1 と l」「0 と O」を聞き分ける必要がなくなる。
 * base64url をそのまま見せると、そこが確実に事故る。
 */
export function crockford32(bytes: Uint8Array, length: number): string {
  let out = "";
  let bits = 0;
  let value = 0;

  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += CROCKFORD[(value >>> (bits - 5)) & 31];
      bits -= 5;
      if (out.length >= length) return out;
    }
  }
  if (bits > 0 && out.length < length) out += CROCKFORD[(value << (5 - bits)) & 31];
  return out.slice(0, length);
}

/**
 * ULID 生成(同期キューの冪等キー用 / CLAUDE.md §4-4)。
 * 48bit タイムスタンプ + 80bit ランダム、Crockford Base32 26文字。
 */
export function ulid(now: number = Date.now()): string {
  let ts = "";
  let t = now;
  for (let i = 0; i < 10; i++) {
    ts = CROCKFORD[t % 32]! + ts;
    t = Math.floor(t / 32);
  }
  const rand = new Uint8Array(16);
  crypto.getRandomValues(rand);
  let body = "";
  for (let i = 0; i < 16; i++) body += CROCKFORD[rand[i]! % 32]!;
  return ts + body;
}
